import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Star,
  Heart,
  MessageSquare,
  FileText,
  MapPin,
  Clock,
  Calendar,
  Award,
  Download,
  Filter,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import type { EmployerApplicant } from '../../api/employerApplicants';

type PipelineStatus = 'new' | 'reviewing' | 'shortlisted' | 'interviewed' | 'rejected' | 'hired';

type Applicant = EmployerApplicant & {
  phone?: string;
  skillTestScore?: number;
  skillTestPassed?: boolean;
};

interface Job {
  id: number | string;
  title: string;
  location: string;
  salary: string;
  type: string;
  posted: string;
  applicants: number;
  views: number;
  status: string;
}

interface ViewApplicantsProps {
  job: Job;
  applicants: Applicant[];
  onClose: () => void;
  loading?: boolean;
}

const toKey = (value: string | number) => value.toString();

const STATUS_LABELS: Record<PipelineStatus, string> = {
  new: 'New Applicant',
  reviewing: 'Under Review',
  shortlisted: 'Shortlisted',
  interviewed: 'Interviewed',
  rejected: 'Not Selected',
  hired: 'Hired',
};

const normalizeStatus = (status?: string): PipelineStatus => {
  if (!status) return 'new';
  const normalized = status.toLowerCase();
  if (normalized === 'interview') return 'interviewed';
  if (normalized === 'pending') return 'reviewing';
  if (normalized === 'applied') return 'reviewing';
  if (['new', 'reviewing', 'shortlisted', 'interviewed', 'rejected', 'hired'].includes(normalized)) {
    return normalized as PipelineStatus;
  }
  return 'reviewing';
};

export function ViewApplicants({ job, applicants, onClose, loading }: ViewApplicantsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [shortlistedIds, setShortlistedIds] = useState<string[]>([]);
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, PipelineStatus | undefined>>({});
  const isShortlisted = (id: string | number) => shortlistedIds.includes(toKey(id));
  const resolveApplicantStatus = (applicant: Applicant) =>
    statusOverrides[toKey(applicant.id)] ?? normalizeStatus(applicant.status);

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-sm text-gray-400">
        Loading applicants…
      </div>
    );
  }

  if (!applicants.length) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-gray-400">
        <Users className="h-10 w-10 text-gray-500" />
        <p>No applicants yet. Invite candidates or check back later.</p>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const toggleShortlist = (id: number | string) => {
    const key = toKey(id);
    const previousSnapshot = [...shortlistedIds];
    const wasShortlisted = previousSnapshot.includes(key);
    const updated = wasShortlisted
      ? previousSnapshot.filter(candidateId => candidateId !== key)
      : [...previousSnapshot, key];

    setShortlistedIds(updated);

    toast.success(
      wasShortlisted ? 'Removed from shortlist' : 'Added to shortlist',
      {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => setShortlistedIds(previousSnapshot),
        },
      },
    );
  };

  const updateApplicantStatus = (applicantId: number | string, newStatus: PipelineStatus) => {
    if (!newStatus) {
      return;
    }

    const key = toKey(applicantId);
    const previousOverride = statusOverrides[key];
    const applicantRecord = applicants.find((applicant) => toKey(applicant.id) === key);
    const previousResolved = previousOverride ?? normalizeStatus(applicantRecord?.status);

    if (previousResolved === newStatus) {
      toast.info(`Applicant already marked as ${newStatus}.`);
      return;
    }

    setStatusOverrides((prev) => ({
      ...prev,
      [key]: newStatus,
    }));

    toast.success(`Applicant status updated to ${newStatus}`, {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () =>
          setStatusOverrides((prev) => {
            const next = { ...prev };
            if (previousOverride !== undefined) {
              next[key] = previousOverride;
            } else {
              delete next[key];
            }
            return next;
          }),
      },
      description: `Previous status: ${previousResolved}`,
    });
  };

  const downloadAllResumes = () => {
    toast.loading('Preparing resumes for download...');
    setTimeout(() => {
      toast.dismiss();
      toast.success('All resumes downloaded successfully!');
    }, 1500);
  };

  const exportApplicants = () => {
    toast.loading('Preparing applicant data export...');
    setTimeout(() => {
      toast.dismiss();
      toast.success('Applicant data exported to CSV');
    }, 1500);
  };

  const sendBulkMessage = () => {
    toast.success('Bulk message sent to selected applicants');
  };

  // Filter and sort applicants
  const filteredApplicants = applicants
    .filter((applicant) => {
      const key = toKey(applicant.id);
      const skills = Array.isArray(applicant.skills) ? applicant.skills : [];
      const matchesSearch =
        applicant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        applicant.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        skills.some((skill) => skill.toLowerCase().includes(searchQuery.toLowerCase()));

      const resolvedStatus = statusOverrides[key] ?? normalizeStatus(applicant.status);
      const matchesFilter =
        filterStatus === 'all' ||
        (filterStatus === 'shortlisted' && (isShortlisted(applicant.id) || resolvedStatus === 'shortlisted')) ||
        (filterStatus === 'high-match' && applicant.aiScore >= 85) ||
        (filterStatus === 'test-passed' && applicant.skillTestPassed);

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'match-score':
          return b.aiScore - a.aiScore;
        case 'test-score':
          return (b.skillTestScore || 0) - (a.skillTestScore || 0);
        case 'oldest':
          return 1;
        case 'newest':
        default:
          return -1;
      }
    });

  const stats = {
    total: applicants.length,
    shortlisted: applicants.reduce((count, applicant) => {
      const key = toKey(applicant.id);
      const resolvedStatus = statusOverrides[key] ?? normalizeStatus(applicant.status);
      if (isShortlisted(applicant.id) || resolvedStatus === 'shortlisted') {
        return count + 1;
      }
      return count;
    }, 0),
    highMatch: applicants.filter((a) => a.aiScore >= 85).length,
    testPassed: applicants.filter((a) => a.skillTestPassed).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="text-white mb-1">{job.title}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {job.location}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Posted {job.posted}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {applicants.length} Applicants
              </span>
            </div>
          </div>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
            {job.status}
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Applicants', value: stats.total, icon: Users, color: 'blue' },
          { label: 'Shortlisted', value: stats.shortlisted, icon: Star, color: 'yellow' },
          { label: 'High Match (>85%)', value: stats.highMatch, icon: TrendingUp, color: 'green' },
          { label: 'Test Passed', value: stats.testPassed, icon: CheckCircle2, color: 'purple' },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-${stat.color}-500/20 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${stat.color}-400`} />
                  </div>
                  <div>
                    <div className="text-white text-2xl">{stat.value}</div>
                    <p className="text-xs text-gray-400">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search applicants by name, title, or skills..."
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
          />
        </div>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full lg:w-48 bg-white/10 border-white/20 text-white">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
            <SelectItem value="all">All Applicants</SelectItem>
            <SelectItem value="shortlisted">Shortlisted</SelectItem>
            <SelectItem value="high-match">High Match (&gt;85%)</SelectItem>
            <SelectItem value="test-passed">Test Passed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full lg:w-48 bg-white/10 border-white/20 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="match-score">Highest Match</SelectItem>
            <SelectItem value="test-score">Highest Test Score</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={downloadAllResumes}
          className="border-white/20 text-white hover:bg-white/5"
        >
          <Download className="w-3 h-3 mr-1" />
          Download All Resumes
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={exportApplicants}
          className="border-white/20 text-white hover:bg-white/5"
        >
          <Download className="w-3 h-3 mr-1" />
          Export to CSV
        </Button>
        {shortlistedIds.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={sendBulkMessage}
            className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10"
          >
            <Mail className="w-3 h-3 mr-1" />
            Message Shortlisted ({shortlistedIds.length})
          </Button>
        )}
      </div>

      {/* Applicants List */}
      <div className="space-y-4">
        {filteredApplicants.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-white mb-2">No applicants found</h3>
              <p className="text-gray-400 text-sm">
                {searchQuery || filterStatus !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : 'Applications will appear here once candidates apply'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredApplicants.map((applicant) => {
            const currentStatus = statusOverrides[toKey(applicant.id)] ?? normalizeStatus(applicant.status);
            const skills = Array.isArray(applicant.skills) ? applicant.skills : [];
            const matchReasons = Array.isArray(applicant.matchReasons) ? applicant.matchReasons : [];
            const appliedDate = applicant.appliedDate
              ? new Date(applicant.appliedDate).toLocaleDateString()
              : 'Recently';

            return (
            <Card
              key={applicant.id}
              className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Applicant Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4 mb-4">
                      <Avatar className="w-16 h-16">
                        <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black text-xl">
                          {applicant.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-white">{applicant.name}</h4>
                              <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                                <Award className="w-3 h-3 mr-1" />
                                {applicant.aiScore}% Match
                              </Badge>
                              {applicant.skillTestPassed && (
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Test Passed
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-300 mb-2">{applicant.title}</p>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {applicant.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {applicant.experience}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Applied {appliedDate}
                              </span>
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-gray-300 mb-3 line-clamp-2">
                          {applicant.summary || 'Candidate summary coming soon.'}
                        </p>

                        {/* Skills */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {skills.slice(0, 5).map((skill, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="border-white/20 text-gray-300 text-xs"
                            >
                              {skill}
                            </Badge>
                          ))}
                          {skills.length > 5 && (
                            <Badge
                              variant="outline"
                              className="border-white/20 text-gray-300 text-xs"
                            >
                              +{skills.length - 5} more
                            </Badge>
                          )}
                        </div>

                        {/* Skill Test Score */}
                        {applicant.skillTestScore !== undefined && (
                          <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white/10">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-gray-400">Skill Test Score</span>
                              <span className={`text-sm ${applicant.skillTestPassed ? 'text-green-400' : 'text-[#ba9036]'}`}>
                                {applicant.skillTestScore}%
                              </span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${applicant.skillTestPassed ? 'bg-green-500' : 'bg-[#ba9036]'}`}
                                style={{ width: `${applicant.skillTestScore}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Match Reasons */}
                        <div className="mb-4">
                          <p className="text-xs text-gray-400 mb-2">Match Reasons:</p>
                          <div className="space-y-1">
                            {matchReasons.slice(0, 3).map((reason, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-xs text-gray-300">
                                <CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                                <span>{reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => toggleShortlist(applicant.id)}
                            variant={isShortlisted(applicant.id) ? "default" : "outline"}
                            size="sm"
                            className={
                              isShortlisted(applicant.id)
                                ? "bg-gold-gradient text-black"
                                : "border-white/20 text-white hover:bg-white/5"
                            }
                          >
                            <Heart className={`w-3 h-3 mr-1 ${isShortlisted(applicant.id) ? 'fill-current' : ''}`} />
                            {isShortlisted(applicant.id) ? 'Shortlisted' : 'Shortlist'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Message
                          </Button>
                          <Link to={`/employer/candidate/${applicant.id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-white/20 text-white hover:bg-white/5"
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              View Profile
                            </Button>
                          </Link>
                          <Select
                            value={currentStatus}
                            onValueChange={(value) => updateApplicantStatus(applicant.id, value as PipelineStatus)}
                          >
                            <SelectTrigger className="h-8 w-auto border-white/20 text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="reviewing">Reviewing</SelectItem>
                              <SelectItem value="shortlisted">Shortlisted</SelectItem>
                              <SelectItem value="interviewed">Interviewed</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                              <SelectItem value="hired">Hired</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })
        )}
      </div>

      {/* Close Button */}
      <div className="flex justify-end pt-4 border-t border-white/10">
        <Button
          variant="outline"
          onClick={onClose}
          className="border-white/20 text-white hover:bg-white/5"
        >
          Close
        </Button>
      </div>
    </div>
  );
}
